import {
  QStack,
  useCurrentUser,
  QFormControl,
  QInput,
  QButton,
  useToastProvider,
  QBackLink,
  QSelect,
} from '@qualio/ui-components';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Field, FieldProps, Form, Formik } from 'formik';
import { createErrorToast } from '../../../utils/toast.utils';
import { getErrorMessageOrDefault } from '../../../errors/um-errors';
import { createDevApiToken } from '../../../api/medtech-api';
import { Header } from '../../../containers/header';
import { NavList, UsergroupsList } from '../../../utils/umfe.enums';
import { CacheKeys, Group } from '../../../types';
import { PermissionsFormElements } from '../../../components/permissions/PermissionsFormElements';
import { QualityEventsPermissionsFormFields } from '../../../components/FormComponents/QualityEventsPermissions';
import { SuppliersPermissionsFormFields } from '../../../components/FormComponents/SuppliersPermissions';
import { APICapabilitiesPermissionsFormFields } from '../../../components/FormComponents/APICapabilitiesPermissions';
import { DesignControlsPermissionsFormFields } from '../../../components/FormComponents/DesignControlsPermissions';
import { CompliancePermissionsFormFields } from '../../../components/FormComponents/CompliancePermissions';
import CheckBox from '../../../components/FormComponents/CheckBox';
import {
  CanAccessAPICapabilities,
  CanAccessCompliance,
  CanAccessDesignControls,
  CanAccessSuppliers,
  CanCreateIssue,
  CanManageQualityEventWorkflow,
  CanViewQE,
  CanWorkOnIssue,
} from '../../../utils/validation/medtechPermissions';
import { TokenModal } from './TokenModal';
import { GroupsSelector } from './GroupsSelector';

type TokenFormValues = {
  tokenName: string;
  role: (typeof UsergroupsList)[keyof typeof UsergroupsList];
  isAdmin: boolean;
} & QualityEventsPermissionsFormFields &
  SuppliersPermissionsFormFields &
  APICapabilitiesPermissionsFormFields &
  DesignControlsPermissionsFormFields &
  CompliancePermissionsFormFields;

const initialValues: TokenFormValues = {
  tokenName: '',
  role: UsergroupsList.QUALITY,
  isAdmin: false,
  canViewQE: false,
  canCreateQE: false,
  canEditQE: false,
  canManageQE: false,
  canAccessSuppliers: false,
  canAccessDesignControls: false,
  canAccessAPICapabilities: false,
  canAccessCompliance: false,
};

const formValidation = (values: any, t: any) => {
  const errors: Partial<TokenFormValues> = {};
  if (values.tokenName === '') {
    errors.tokenName = t('messages.validation.invalidTokenName');
  }
  return errors;
};

export const CreateDevApiToken: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();
  const [tokenValue, setTokenValue] = useState<string>();
  const [role, setRole] = useState(UsergroupsList.QUALITY);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const roleSelectOptions = useMemo(
    () =>
      Object.values(UsergroupsList).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );

  const createToken = (name: string, isAdmin: boolean, permissions: Record<string, boolean>) => {
    setIsLoading(true);
    createDevApiToken(currentUser.companyId, {
      name,
      role: role.value,
      is_admin: isAdmin,
      permissions,
      group_ids: groups.map((g) => g.id),
    })
      .then(async (returnPayload) => {
        setTokenValue(returnPayload.token);
        // QUM-1266 diagnostic — remove after one valpack capture
        try {
          const cache = queryClient.getQueryCache().getAll();
          const payload = JSON.stringify({
            stage: 'pre-invalidate',
            companyId: currentUser.companyId,
            cacheSize: cache.length,
            entries: cache.map((q) => ({
              key: q.queryKey,
              observers: q.getObserversCount(),
              hasData: q.state.data !== undefined,
              isFetching: q.state.isFetching,
              isInvalidated: q.state.isInvalidated,
            })),
          });
          // eslint-disable-next-line no-console
          console.log(`[devapi-diag] ${payload}`);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(`[devapi-diag] diag-failed ${String(e)}`);
        }
        // React Query v3 dedupes invalidateQueries onto an already in-flight list refetch
        // rather than issuing a new one; cancel first so the post-create refetch always runs
        // and the newly created token is guaranteed to appear in the list.
        await queryClient.cancelQueries({ queryKey: [CacheKeys.API_KEYS, currentUser.companyId] });
        await queryClient.invalidateQueries({ queryKey: [CacheKeys.API_KEYS, currentUser.companyId] });
        // eslint-disable-next-line no-console
        console.log('[devapi-diag] post-invalidate-call');
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        const message = getErrorMessageOrDefault(
          error,
          { TOKEN_NAME_EXISTS: t('messages.error.tokenNameExists') },
          t('messages.error.createToken'),
        );
        showToast(createErrorToast(message, 'dev-api-create-token-error-toast'));
        setIsLoading(false);
      });
  };

  const resetPermissions = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    setFieldValue('canViewQE', false);
    setFieldValue('canCreateQE', false);
    setFieldValue('canEditQE', false);
    setFieldValue('canManageQE', false);
    setFieldValue('canAccessSuppliers', false);
    setFieldValue('canAccessAPICapabilities', false);
    setFieldValue('canAccessDesignControls', false);
    setFieldValue('canAccessCompliance', false);
    setFieldValue('isAdmin', false);
    setRole(UsergroupsList.QUALITY);
  };

  const handleSubmitPress = async (event: TokenFormValues) => {
    const permissions: Record<string, boolean> = {
      [CanViewQE]: event.canViewQE,
      [CanCreateIssue]: event.canCreateQE,
      [CanWorkOnIssue]: event.canEditQE,
      [CanManageQualityEventWorkflow]: event.canManageQE,
      [CanAccessSuppliers]: event.canAccessSuppliers,
      [CanAccessAPICapabilities]: event.canAccessAPICapabilities,
      [CanAccessDesignControls]: event.canAccessDesignControls,
      [CanAccessCompliance]: event.canAccessCompliance,
    };
    createToken(event.tokenName, event.isAdmin, permissions);
  };

  const navigateBackToList = () => navigate(NavList.DEV_API.href);

  return (
    <QStack maxWidth="720px">
      <QBackLink href={NavList.DEV_API.href} data-cy="back-api">
        {t('devApi.backToTokens')}
      </QBackLink>
      <Header content={t('devApi.createToken')} />
      {tokenValue && <TokenModal token={tokenValue} onClose={navigateBackToList} />}
      <Formik
        initialValues={initialValues}
        validate={(values) => formValidation(values, t)}
        onSubmit={(values) => {
          handleSubmitPress(values);
        }}
      >
        {({ dirty, isValid, setFieldValue }) => (
          <Form>
            <fieldset disabled={isLoading || !!tokenValue} style={{ border: 'none', padding: 0, margin: 0 }}>
              <QStack spacing={4}>
                <QFormControl
                  label={t('forms.labels.name')}
                  helper={t('forms.placeholders.whatIsThisTokenFor')}
                  data-cy="token-name-control"
                >
                  <Field name="tokenName">
                    {({ field }: FieldProps) => {
                      const { value, onChange, onBlur } = field;
                      return (
                        <QInput
                          id="tokenName"
                          name="tokenName"
                          data-cy="token-name"
                          data-testid="token-name"
                          maxLength={50}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      );
                    }}
                  </Field>
                </QFormControl>
                <QFormControl
                  label={t('forms.labels.role')}
                  helper={t('devApi.tokenRoleHelper')}
                  data-cy="role-control"
                >
                  <QSelect
                    data-cy="select-role"
                    data-testid="select-role"
                    options={roleSelectOptions}
                    value={role.value}
                    onChange={(v) => {
                      if (v) {
                        const matchingRole = Object.values(UsergroupsList).find(
                          (roleItem) => roleItem.value === v.value,
                        );
                        if (matchingRole) {
                          setRole(matchingRole);
                        }
                      }
                    }}
                  />
                </QFormControl>
                <CheckBox
                  name="isAdmin"
                  data-testid="isAdmin"
                  label={t('forms.labels.adminAccess')}
                  text={t('forms.labels.adminAccess')}
                  formLabel={t('forms.labels.adminAccess')}
                  helper={t('devApi.adminAccessHelper')}
                  data-cy="isAdmin"
                />
                <QFormControl
                  label={t('forms.labels.groups')}
                  helper={t('devApi.tokenGroupsHelper')}
                  data-cy="groups-control"
                >
                  <GroupsSelector onSelect={setGroups} selectedGroups={groups} />
                </QFormControl>
                <PermissionsFormElements resetPermissions={() => resetPermissions(setFieldValue)} isHumanUser={false} />

                <QStack direction="row">
                  <QButton
                    data-cy="create-now-button"
                    type="submit"
                    isLoading={isLoading}
                    isDisabled={!!tokenValue || !dirty || !isValid || isLoading}
                  >
                    {t('developerApi.createToken')}
                  </QButton>
                  <QButton onClick={navigateBackToList} variant="outline">
                    {t('common.cancel')}
                  </QButton>
                </QStack>
              </QStack>
            </fieldset>
          </Form>
        )}
      </Formik>
    </QStack>
  );
};
